import { Fragment, useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { transitionState, useNavigateTransition } from '../lib/navigation';
import CameraCapture from '../components/CameraCapture';
import PhotoCropper from '../components/PhotoCropper';
import { fullName, useFlow } from '../state/flow';
import { isCameraSupported } from '../lib/photo';
import { trackEvent } from '../lib/analytics';
import './Photo.scss';

type Mode = 'intro' | 'camera' | 'cropping' | 'confirm';

export function PhotoLeft() {
  const { t } = useTranslation();
  const { form } = useFlow();

  // Mirrors FormLeft's form-stack structure so the head's gray-band /
  // body's white-band split reads identically and the form-stack centers at
  // the same vertical position once the body min-height reserves the form's
  // natural height.
  return (
    <div className="form-stack">
      <div className="form__head">
        <div className="form__head-inner">
          <p className="form__kicker">{t('form.kicker')}</p>
          <h1 className="form__title">{t('form.title')}</h1>
        </div>
      </div>
      <div className="form__body">
        <dl className="photo-summary">
          <div className="photo-summary__field">
            <dt className="photo-summary__label">{t('card.name')}</dt>
            <dd className="photo-summary__value">{fullName(form)}</dd>
          </div>
          <div className="photo-summary__field">
            <dt className="photo-summary__label">
              {t('card.label', { label: t('card.major') })}
            </dt>
            <dd className="photo-summary__value">{form.major}</dd>
          </div>
          <div className="photo-summary__field">
            <dt className="photo-summary__label">
              {t('card.label', { label: t('card.minor') })}
            </dt>
            <dd className="photo-summary__value">{form.minor}</dd>
          </div>
          <div className="photo-summary__field">
            <dt className="photo-summary__label">
              {t('card.label', { label: t('form.club') })}
            </dt>
            <dd className="photo-summary__value photo-summary__value--clubs">
              {form.clubs.map((club, i) => (
                <Fragment key={club}>
                  {i > 0 && <span className="photo-summary__sep">, </span>}
                  <span className="photo-summary__club">{club}</span>
                </Fragment>
              ))}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

interface PhotoRightProps {
  /** True when /photo is the active route. Resets transient state on deactivation. */
  active: boolean;
}

export function PhotoRight({ active }: PhotoRightProps) {
  const { t } = useTranslation();
  // Plain navigate within the form↔photo flow; view transition for /loading.
  const navigatePlain = useNavigate();
  const navigateTransition = useNavigateTransition();
  const { setPhoto } = useFlow();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const flashTimerRef = useRef<number | null>(null);
  const [mode, setMode] = useState<Mode>('intro');
  const [uploadSrc, setUploadSrc] = useState<string | null>(null);
  const [croppedDataUrl, setCroppedDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flashing, setFlashing] = useState(false);
  const [cameraOk] = useState(isCameraSupported);

  useEffect(
    () => () => {
      if (uploadSrc?.startsWith('blob:')) URL.revokeObjectURL(uploadSrc);
    },
    [uploadSrc],
  );

  useEffect(
    () => () => {
      if (flashTimerRef.current !== null) window.clearTimeout(flashTimerRef.current);
    },
    [],
  );

  const triggerFlash = useCallback(() => {
    setFlashing(true);
    if (flashTimerRef.current !== null) window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => {
      setFlashing(false);
      flashTimerRef.current = null;
    }, 400);
  }, []);

  function handlePickFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    transitionState(
      () => {
        setUploadSrc(url);
        setMode('cropping');
        setError(null);
      },
      ['inner-swap'],
    );
    trackEvent('photo_uploaded');
    e.target.value = '';
  }

  function handleConfirm(dataUrl: string, source: 'camera' | 'upload' = 'upload') {
    // Upload flow has already framed the photo in the cropper, so skip the
    // separate confirm step and advance straight to /loading. Camera flow
    // still goes through confirm so the user can review/retake.
    if (source === 'upload') {
      setPhoto(dataUrl);
      navigateTransition('/loading', undefined, ['route-fade']);
      return;
    }
    trackEvent('photo_captured');
    // The white flash overlay is at peak opacity around the capture moment,
    // so the camera → confirm DOM swap is invisible underneath it. No view
    // transition is needed and one would just compete with the flash as it
    // fades out.
    setCroppedDataUrl(dataUrl);
    setMode('confirm');
  }

  function handleAccept() {
    if (!croppedDataUrl) return;
    setPhoto(croppedDataUrl);
    navigateTransition('/loading', undefined, ['route-fade']);
  }

  function handleRetake() {
    transitionState(
      () => {
        setCroppedDataUrl(null);
        setUploadSrc(null);
        setMode('intro');
      },
      ['inner-swap'],
    );
  }

  // Reset transient capture state on back nav so a return trip lands on the
  // intro screen instead of resurrecting whatever mode was last open.
  function handleBack() {
    setMode('intro');
    setCroppedDataUrl(null);
    setUploadSrc((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
    setError(null);
    navigatePlain('/id-card');
  }

  return (
    <>
      {flashing && <div className="photo__flash" aria-hidden="true" />}
      <div className="photo-right">
        {mode === 'intro' && (
          <div className="photo-intro">
            <h2 className="photo-intro__title">{t('photoIntro.title')}</h2>
            <div className="photo-intro__actions">
              {cameraOk && (
                <button
                  type="button"
                  className="photo-intro__btn"
                  onClick={() => transitionState(() => setMode('camera'), ['inner-swap'])}
                  aria-label={t('photoIntro.useCamera')}
                >
                  <img src="/img/camera.svg" alt="" />
                </button>
              )}
              <button
                type="button"
                className="photo-intro__btn"
                onClick={() => fileInputRef.current?.click()}
                aria-label={t('photoIntro.uploadPhoto')}
              >
                <img src="/img/upload.svg" alt="" />
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handlePickFile}
            />
            <button type="button" className="photo-intro__back" onClick={handleBack}>
              ← {t('photoIntro.back')}
            </button>
            {error && <p className="photo-intro__error">{error}</p>}
          </div>
        )}

        {mode === 'camera' && active && (
          <CameraCapture
            onCapture={(dataUrl) => handleConfirm(dataUrl, 'camera')}
            onShutter={triggerFlash}
            onBack={handleRetake}
            onError={(kind) => {
              transitionState(
                () => {
                  setError(
                    kind === 'denied' ? t('photo.cameraDenied') : t('photo.cameraUnavailable'),
                  );
                  setMode('intro');
                },
                ['inner-swap'],
              );
            }}
          />
        )}

        {mode === 'cropping' && uploadSrc && (
          <PhotoCropper src={uploadSrc} onConfirm={handleConfirm} onBack={handleRetake} />
        )}

        {mode === 'confirm' && croppedDataUrl && (
          <div className="photo-confirm">
            <div className="photo-confirm__frame">
              <img src={croppedDataUrl} alt="" />
            </div>
            <div className="photo-confirm__controls">
              <button type="button" className="photo-confirm__primary" onClick={handleAccept}>
                {t('common.continue')}
              </button>
              <button type="button" className="photo-confirm__secondary" onClick={handleRetake}>
                ← {t('photo.retake')}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

